import { type ApolloError } from '@apollo/client';
import { Form, FormTextInput, StandardFormButtons } from '@components/form';
import { useCreateSecretMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Secret name is required')
    .matches(
      /^[a-zA-Z_][a-zA-Z0-9_]*$/,
      'Secret name can only contain letters, numbers, and underscores. Must start with a letter or underscore.',
    ),
  value: yup.string().required('Secret value is required'),
});

export const CreateSecretModal = ({
  organizationId,
  workspaceId,
  onCreate,
  onClose,
}: {
  organizationId?: string;
  workspaceId?: string;
  onCreate?: VoidFunction;
  onClose?: () => void;
}) => {
  const { createAsyncNotification } = useNotifications();
  const [createSecretMutation] = useCreateSecretMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const secretType = workspaceId ? 'workspace' : organizationId ? 'organization' : 'user';

  const createSecret = async ({ name, value }: { name: string; value: string }) => {
    posthog.capture('agent-studio:user_create-secret_submitted', {
      organizationId: organizationId || undefined,
      workspaceIds: workspaceId ? [workspaceId] : undefined,
    });
    await createSecretMutation({
      variables: {
        organizationId: organizationId || undefined,
        workspaceIds: secretType === 'workspace' ? [workspaceId] : undefined,
        includeInAllWorkspaces: secretType === 'organization',
        name,
        value,
      },
      onCompleted: () => {
        posthog.capture('agent-studio:user_create-secret_completed', {
          organizationId: organizationId || undefined,
          workspaceIds: workspaceId ? [workspaceId] : undefined,
        });
        onCreate?.();
        onClose();
      },
      onError: (error: ApolloError) => {
        posthog.capture('agent-studio:user_create-secret_failed', {
          organizationId: organizationId || undefined,
          workspaceIds: workspaceId ? [workspaceId] : undefined,
          error: (error as Error)?.message || 'An error occurred during creation',
        });
        setFormError(error.message);
        throw error;
      },
    });
  };

  const handleFormSubmit = createAsyncNotification(
    `${secretType.charAt(0).toUpperCase()}${secretType.slice(1)} secret created successfully.`,
    createSecret,
    {
      loadingMessage: `Creating ${secretType} secret...`,
    },
  );

  return (
    <Form
      initialValues={{
        name: '',
        value: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {() => {
        return (
          <Stack style={{ maxWidth: '378px' }}>
            <FormTextInput name="name" label="Name" autoComplete="off" />
            <FormTextInput name="value" label="Value" multiline rows={3} autoComplete="off" />
            <StandardFormButtons onClose={onClose} formError={formError} submitLabel="Create" />
          </Stack>
        );
      }}
    </Form>
  );
};
